import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ImpersonationService } from "common/services";

const SESSION_KEY = "nr.impersonation";

class ImpersonationStore {
    @observable.ref session = null;

    _impersonationService = null;

    get impersonationService() {
        if (!this._impersonationService) {
            this._impersonationService = this.rootStore.createApplicationService(ImpersonationService);
        }
        return this._impersonationService;
    }

    @computed get isImpersonating() {
        return !!this.session;
    }

    @computed get impersonatedUser() {
        return this.session ? this.session.target : null;
    }

    constructor(rootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
        this.session = this._readSession();
    }

    _readSession() {
        try {
            const raw = sessionStorage.getItem(SESSION_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }

    @action.bound
    async startImpersonation(userId) {
        if (this.isImpersonating) {
            throw new Error("Already impersonating a user. Stop the current session first.");
        }
        const { application, userStore, routerStore } = this.rootStore;
        const baasic = application.baasic;

        const adminToken = baasic.getAccessToken();
        const response = await this.impersonationService.start(userId);
        const { accessToken, target } = response.data;

        const session = { adminToken, target };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
        runInAction(() => {
            this.session = session;
        });

        baasic.tokenHandler.store(accessToken);
        userStore.removeUser();
        await userStore.resolveUser();
        // Rebuild the left menu for the impersonated user — buildMenu() branches on
        // the user's roles, and it otherwise only runs once at login (admin's menu).
        this.rootStore.applicationMainSectionStore.buildMenu();
        await routerStore.goTo(routerStore.initialState);
    }

    @action.bound
    async stopImpersonation() {
        const session = this.session || this._readSession();
        if (!session) {
            return;
        }
        const { application, userStore, routerStore } = this.rootStore;
        const baasic = application.baasic;

        sessionStorage.removeItem(SESSION_KEY);
        runInAction(() => {
            this.session = null;
        });

        baasic.tokenHandler.store(session.adminToken);
        userStore.removeUser();
        await userStore.resolveUser();
        // Rebuild the menu back to the admin's after restoring the admin session.
        this.rootStore.applicationMainSectionStore.buildMenu();
        await routerStore.goTo("master.administration.dashboard");
    }
}

export default ImpersonationStore;
